import api from './api';
import type {
  Signal,
  CreateSignalRequest,
  ReviewSignalRequest,
  PaginatedResponse,
  SignalStatus,
  AnalysisDirection,
  Timeframe,
} from '@trading/shared';

interface SignalWithNotificationStatus extends Signal {
  viewed?: boolean;
  dismissed?: boolean;
}

export const signalsService = {
  // Create a new signal (admin/expert only)
  async createSignal(data: CreateSignalRequest): Promise<Signal> {
    const response = await api.post<Signal>('/signals', data);
    return response.data;
  },

  // Get all signals (filtered by status for admin)
  async getSignals(
    page = 1,
    limit = 20,
    status: SignalStatus | 'all' = 'all'
  ): Promise<PaginatedResponse<Signal>> {
    const response = await api.get<PaginatedResponse<Signal>>(
      '/signals',
      { params: { page, limit, status } }
    );
    return response.data;
  },

  // Get approved signals for regular users
  async getApprovedSignals(
    page = 1,
    limit = 20
  ): Promise<PaginatedResponse<SignalWithNotificationStatus>> {
    const response = await api.get<PaginatedResponse<SignalWithNotificationStatus>>(
      '/signals/approved',
      { params: { page, limit } }
    );
    return response.data;
  },

  // Get new (undismissed) signals for popup
  async getNewSignals(): Promise<Signal[]> {
    const response = await api.get<Signal[]>('/signals/new');
    return response.data;
  },

  // Get signal by ID
  async getSignal(id: string): Promise<Signal> {
    const response = await api.get<Signal>(`/signals/${id}`);
    return response.data;
  },

  // Review signal (approve/reject)
  async reviewSignal(id: string, data: ReviewSignalRequest): Promise<Signal> {
    const response = await api.put<Signal>(`/signals/${id}/review`, data);
    return response.data;
  },

  // Dismiss signal notification
  async dismissSignal(id: string): Promise<void> {
    await api.post(`/signals/${id}/dismiss`);
  },

  // Delete signal (admin only)
  async deleteSignal(id: string): Promise<void> {
    await api.delete(`/signals/${id}`);
  },

  // Get pending signals count
  async getPendingCount(): Promise<number> {
    const response = await api.get<{ count: number }>('/signals/pending/count');
    return response.data.count;
  },

  // Send a signal immediately from the current analysis (admin/expert only).
  // Uses autoApprove=true so the signal is broadcast to all users in one step.
  async sendSignalFromAnalysis(input: SendSignalFromAnalysisInput): Promise<SendSignalResponse> {
    const payload: CreateSignalRequest = {
      asset: input.asset,
      timeframe: input.timeframe,
      direction: input.direction,
      entryPrice: input.entryPrice,
      stopLoss: input.stopLoss,
      takeProfit1: input.takeProfit1,
      takeProfit2: input.takeProfit2,
      takeProfit3: input.takeProfit3,
      confidence: input.confidence,
      narrative: input.narrative,
      analysis: input.narrative,
      expertNotes: input.expertNotes,
      autoApprove: true,
    };
    const response = await api.post<{
      success: boolean;
      data: Signal;
      notifiedCount?: number;
    }>('/signals', payload);
    return {
      signal: response.data.data,
      notifiedCount: response.data.notifiedCount,
    };
  },
};

export interface SendSignalFromAnalysisInput {
  asset: string;
  timeframe: Timeframe;
  direction: AnalysisDirection;
  entryPrice: number;
  stopLoss: number;
  takeProfit1: number;
  takeProfit2?: number;
  takeProfit3?: number;
  confidence: number;
  narrative: string;
  /** Optional free-text guidance from the expert. Whitespace-trimmed; omit when empty. */
  expertNotes?: string;
}

export interface SendSignalResponse {
  signal: Signal;
  /** Number of users that received at least one notification channel, if returned by the API. */
  notifiedCount?: number;
}
